import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Loader2,
  Swords,
  ArrowRight,
  X,
  ArrowLeft
} from 'lucide-react';
import { DIFFICULTIES } from '../../lib/constants';
import { cn, isMobileDevice } from '../../lib/utils';
import { logger } from '../../lib/utils';
import axios from 'axios';
import { API } from './challengeUtils';

export function ChallengeInfoModal({ 
  isOpen, 
  onClose, 
  challengeCode,
  onPlay 
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState(null);
  
  const loadChallenge = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/challenges/${challengeCode}?is_mobile=${isMobileDevice()}`);
      setChallenge(response.data);
    } catch (err) {
      logger.error('Failed to load challenge:', err);
      setError(err.response?.status === 404 ? 'Challenge not found' : 'Failed to load challenge');
    } finally {
      setIsLoading(false);
    }
  }, [challengeCode]);
  
  useEffect(() => {
    if (isOpen && challengeCode) {
      loadChallenge();
    }
  }, [isOpen, challengeCode, loadChallenge]);
  
  const handlePlay = () => {
    if (challenge) {
      onPlay(challenge.image_url, challenge.difficulty, challengeCode);
      onClose();
    }
  };
  
  const diffConfig = challenge ? DIFFICULTIES[challenge.difficulty] : null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto max-h-[90vh] sm:max-h-[85vh] overflow-y-auto box-border" data-testid="challenge-info-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Swords className="h-5 w-5 text-primary" />
            Friend Challenge
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground mt-2">Loading challenge...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-destructive font-medium">{error}</p>
            <Button variant="outline" onClick={onClose} className="mt-4">
              Close
            </Button>
          </div>
        ) : challenge ? (
          <div className="space-y-4 pt-2">
            {/* Challenge Preview */}
            <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
              <img 
                src={challenge.image_url} 
                alt="Challenge puzzle" 
                className="w-20 h-20 rounded-lg object-cover"
              />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  Challenge by <span className="font-medium text-foreground">{challenge.creator_name || 'Anonymous'}</span>
                </p>
                <Badge 
                  variant="outline" 
                  className={cn(
                    "mt-1",
                    challenge.difficulty === 'easy' && "bg-success/10 text-success border-success/30",
                    challenge.difficulty === 'normal' && "bg-primary/10 text-primary border-primary/30",
                    challenge.difficulty === 'hard' && "bg-destructive/10 text-destructive border-destructive/30"
                  )}
                >
                  {diffConfig?.name} Difficulty
                </Badge>
                <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  <span>{challenge.participant_count} {challenge.participant_count === 1 ? 'player' : 'players'}</span>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Complete this puzzle and see how you rank against other players!
            </p>
            
            <Button 
              onClick={handlePlay}
              className="w-full h-11 bg-gradient-to-r from-primary to-accent hover:opacity-90"
              data-testid="play-challenge-btn"
            >
              <Swords className="h-4 w-4 mr-2" />
              Accept Challenge
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
